import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { observer } from 'mobx-react-lite';
import SkeltonAnimation from '../../../app/layout/SkeltonAnimation';
import { format } from 'date-fns';
import { Avatar, Box, Card, CardContent, CardHeader, Grid, Stack, styled, Typography, useMediaQuery } from '@mui/material';
import { AdminUser } from '../../../app/models/admin';
import { deepOrange, green, yellow } from '@mui/material/colors';
import AppUserCard from './AppUserCard';
import ModalStore from '../../../app/stores/modalStore';
import { admin, basic, UserActivityStatus } from '../../../app/common/options/adminOptions';

interface Props {
    users: AdminUser[];
    loading: boolean;
    modalStore: ModalStore;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#DEEDD6',
        color: '#072B02',
        fontSize: 13,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        color: '#072B02',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#bce2ca'
    },
    '&:nth-of-type(even)': {
        backgroundColor: '#a8d9c6'
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));


const gridContainer = {
    xs: 1,
    sm: 2,
    md: 3
} as const;

const textGen = (title: string, value: string, xs: number, sm: number) => {
    return <Grid item xs={xs} sm={sm} key={title}>
        <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {title}&nbsp;{value || "---"}
        </Stack>
    </Grid>
}
export default observer(function AppUserRecord({ users, loading, modalStore }: Props) {

     const { openModal } = modalStore;

    const matches = useMediaQuery('(min-width:900px)');

    if (loading) return <SkeltonAnimation iteration={10} />
    if (!loading && users.length < 1) return <Typography variant='h4'>No records found</Typography>

    return (
        <Box sx={{ flexGrow: 1 }}>
            {matches ?
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Pic</StyledTableCell>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell align="right">Activity</StyledTableCell>
                                <StyledTableCell align="right">Email</StyledTableCell>
                                <StyledTableCell align="right">RegisterDate</StyledTableCell>
                                <StyledTableCell align="right">Role</StyledTableCell>
                                <StyledTableCell align="right">Test&nbsp;Result</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <StyledTableRow
                                    key={user.email}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <StyledTableCell sx={{ cursor: 'pointer' }} component="th" scope="row"
                                        onClick={() => openModal(<AppUserCard user={user} />)}
                                    >
                                        <Avatar
                                            sx={user.role === basic ? { bgcolor: deepOrange[500] }
                                                : user.role === admin ? { bgcolor: green[500] }
                                                    : { bgcolor: yellow[500] }}
                                            alt={user.fullName}
                                            src={user.picUrl}
                                        >
                                            {user.fullName[0]}
                                        </Avatar>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {user.fullName}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">{user.activity || UserActivityStatus(user)}</StyledTableCell>
                                    <StyledTableCell align="right">{user.email}</StyledTableCell>
                                    <StyledTableCell align="right">{format(new Date(user.joinDate), 'dd MMM yyyy')}</StyledTableCell>
                                    <StyledTableCell align="right">{user.role}</StyledTableCell>
                                    <StyledTableCell align="right">{user.testResult ?? 10}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                :
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2} direction="column" justifyContent="center" alignItems="center">
                        {users.map((user) => {
                            return (
                                <Grid item xs={12} key={user.email} sx={{ position: 'relative', pb: 0.5, pr: 0.5, mt: 2, bgcolor: '#234f1e', borderRadius: '100% 0% 29% 71% / 0% 100% 0% 100% ' }} >
                                <Typography component='span' variant='body2' sx={{ position: 'absolute', right: 6, top: 0.1, color: '#fff' }}>
                                    Tier1Asset
                                </Typography>
                                <Card sx={{ width: { xs: 360, sm: 500 }, bgcolor: '#93f4d8', color: '#234f1e' }}>
                                <CardHeader
                                            action={
                                                <Box sx={{ cursor: 'pointer' }} onClick={() => modalStore.openModal(
                                                    <AppUserCard user={user} />)}>
                                                <Avatar
                                                    sx={user.role === basic ? { bgcolor: deepOrange[500] }
                                                        : user.role === admin ? { bgcolor: green[500] }
                                                            : { bgcolor: yellow[500] }}
                                                    alt={user.fullName}
                                                    src={user.picUrl}
                                                    
                                                >
                                                    {user.fullName[0]}
                                                </Avatar>
                                                </Box>
                                            }
                                            title={user.fullName}
                                        />
                                        <CardContent>
                                            <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                                                {textGen('Full Name', user.fullName, 12, 6)}
                                                {textGen('Email', user.email, 12, 6)}
                                                {textGen('Activity', user.activity, 12, 6)}
                                                {textGen('Register Date:', format(new Date(user.joinDate), 'dd MMM yyyy')
                                                    , 12, 6)}
                                                {textGen('Role:', user.role, 12, 6)}
                                                {textGen('Test Result', user.testResult.toString(), 12, 6)}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                </Box>
            }
        </Box>
    );
})
